import { useTranslation } from 'react-i18next';
import { Autocomplete } from '../../components/idsk';
import type { AutocompleteSource } from '../types';
import { searchReferences, useReferenceLabel, type ReferenceOption } from './references';

export interface ReferenceFieldProps {
  id: string;
  source: AutocompleteSource;
  label: string;
  labelLang?: string;
  hint?: string;
  hintLang?: string;
  error?: string;
  required: boolean;
  disabled: boolean;
  disabledHint: React.ReactNode;
  extraDescribedBy?: string;
  formGroupClassName?: string;
  value: string | null;
  /** The id of the chosen record, or undefined when the text no longer matches a chosen record. */
  onChange: (id: string | undefined) => void;
}

/**
 * AUTOCOMPLETE field: the APG combobox searching the source register; the value is the id of the chosen record and
 * the input shows its label. Editing the text clears the value until another suggestion is chosen.
 */
export function ReferenceField({ id, source, label, labelLang, hint, hintLang, error, required, disabled, disabledHint, extraDescribedBy, formGroupClassName, value, onChange }: ReferenceFieldProps) {
  const { t } = useTranslation();
  const current = useReferenceLabel(source, value);
  const initialText = value ? (current.label ?? (current.missing ? t('form.referenceMissing') : '')) : '';
  return (
    <Autocomplete<ReferenceOption>
      id={id}
      label={label}
      labelLang={labelLang}
      labelClassName="govuk-label--s"
      hint={hint ?? t(source === 'INSPECTOR' ? 'form.referenceHint.INSPECTOR' : 'form.referenceHint.SUBJECT')}
      hintLang={hint ? hintLang : undefined}
      error={error}
      required={required}
      search={(q, signal) => searchReferences(source, q, signal)}
      getOptionKey={(o) => o.id}
      getOptionLabel={(o) => o.label}
      getOptionHint={(o) => o.hint}
      initialText={initialText}
      onSelect={(o) => onChange(o.id)}
      onTextChange={() => {
        if (value) onChange(undefined);
      }}
      ariaDisabled={disabled}
      beforeInput={disabledHint}
      extraDescribedBy={extraDescribedBy}
      formGroupClassName={formGroupClassName}
    />
  );
}

/** Read-only display of a reference (summary lists, preview). */
export function ReferenceLabel({ source, id }: { source: AutocompleteSource; id: string }) {
  const { t } = useTranslation();
  const { label, missing } = useReferenceLabel(source, id);
  if (missing) return <span className="govuk-hint">{t('form.referenceMissing')}</span>;
  return <>{label ?? t('common.loading')}</>;
}
